/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React from 'react';
import PropTypes from 'prop-types';
import Control from '../Control/Control';
import Label from '../Label/Label';
import OpenIcon from './baseline_arrow_drop_down_black_24dp.png';
import './Combobox.css';

/**
 * Represents the combobox component.
 */

export default class Combobox extends Control {

    /** Label positions. */
    static get LABELPOSITION() { return {
        TOP:   'top',
        LEFT:  'left'
    };}

    /**
     * Properties.
     */

    static propTypes = {
        source:           PropTypes.array.isRequired,
        idattr:           PropTypes.string,
        nameattr:         PropTypes.string,
        label:            PropTypes.string,
        labelwidth:       PropTypes.number,
        labelposition:    PropTypes.string,
        maxlength:        PropTypes.number,
        lookup:           PropTypes.bool,
        disabled:         PropTypes.bool,
        touppercase:      PropTypes.bool,
        tocapitalize:     PropTypes.bool,
        isrequired:       PropTypes.bool,
        validateontyping: PropTypes.bool,
        validateonblur:   PropTypes.bool,
        requiredmessage:  PropTypes.string,
        placeholder:      PropTypes.string,
        tooltip:          PropTypes.string,
        width:            PropTypes.number,
        comment:          PropTypes.string,
        onSelect:         PropTypes.func
    }

    /**
     * Default property values.
     */

    static defaultProps = {
        requiredmessage:  'Mandatory field'
    }

    /**
     * Default state.
     */

    state = { 
        error:          null, 
        showerror:      false,
        showselector:   false,
        selecteditem:   null
    }
    
    /**
     * Sets the focus to the combobox.
     */
    
    setFocus = () => {
        if (this.props.lookup) {
            this.input.focus();
        }
    }

    /**
     * Gets the value of the combobox.
     *
     * @returns {String}  value   value of the combobox
     */

    getValue = () => {
      
        // Getting the value of the combobox

        return this.input.value;
    }

    /**
     * Gets the selected item of the combobox.
     *
     * @returns {Object}  item   selected item of the combobox
     */

    getItem = () => {
        return this.state.selecteditem;
    }

    /**
     * Sets the value of the combobox.
     *
     * @param {String}  value   value of the combobox
     */

    setValue = (value) => {
      
        // Setting the value of the combobox
        
        for (let i = 0; i < this.props.source.length; i++) {
            if (this.props.idattr ? (this.props.source[i][this.props.idattr] === value) :
            (this.props.source[i] === value)) {
                this.setState({selecteditem: this.props.source[i]});
                let comboValue = this.props.nameattr ? this.props.source[i][this.props.nameattr] : this.props.source[i];

                if (this.props.lookup) {
                    this.input.value = comboValue;
                } else {
                    this.input.innerHTML = comboValue;                        
                }

                break;
            }
        }

        this.validate(value);
    }

    /**
     * Clears the value and the error message of the combobox.
     */

    clearValue = () => {
      
        // Clearing the value

        this.input.value = '';
        this.clearError();
    }

    /**
     * Clears the error message of the combobox.
     */

    clearError = () => {
      
        // Clearing the error

        this.setError();
    }

    /**
     * Sets the error message of the combobox.
     *
     * @param {String}  message   error message that has to show
     */

    setError = (message) => {
      
        // Does the error message specified?

        if (message) {

            // Showing the error message

            this.setState({ error: message }, () => {
                setTimeout(() => {
                    this.setState({ showerror: true });
                }, 20);
            });
        } else {

            // Clearing the error message

            this.setState({ showerror: false }, () => {
                setTimeout(() => {
                    this.setState({ error: null });
                }, 250);
            });
        }
    }

    /**
     * Checks that the value of the combobox is empty or not.
     *
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} empty   specified value or the value of the combobox is
     *                              empty or not
     */

    isEmpty = (value) => {
        
        // Getting the value

        let v = (value || this.getValue());

        // Checking the value

        return (v === null || v === undefined) ? true : v.toString().trim() === '';
    }

    /**
     * Validates the combobox.
     * 
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} valid   specified value or the value of the combobox is
     *                              valid or not
     */

    validate = (value) => {

        // Does the value of the combobox valid?

        if (!(this.props.isrequired && this.isEmpty(value))) {
            if (!value) {
                this.clearError();
            }

            return true;
        }

        // Combobox is invalid

        this.setError(this.getRequiredMessage());
        return false;
    }

    /**
     * Validates the combobox on typing.
     */

    _validateOnTyping = () => {
        if (this.props.validateontyping) {
            this.validate();
        }
    }

    /**
     * Validates the combobox on blur.
     */

    _validateOnBlur = () => {
        if (this.props.validateonblur) {
            this.validate();
        }
    }

    /**
     * Fires the "onEnter" event if the user presses the ENTER key.
     */

    _isEnter = (event) => {
        if ((event.keyCode ? event.keyCode : event.which) === 13 && this.props.onEnter) {
            this.props.onEnter();
            return true;
        }

        return false;
    }

    /**
     * Gets the style of the combobox.
     * 
     * @return  {Object}    style   style of the combobox
     */

    getStyle = () => {

        // Preparing

        let style = this._getStyle();
        
        // Adding style attributes
        
        style.marginBottom = (this.props.bottomgap || 0) + 16 + 'px';

        // Returns the style

        return style;
    }

    /**
     * Shows the selector.
     * 
     * @param   {Object}    event       event object
     */

    _showSelector = (event) => {

        // Does the component disabled?

        if (!this.props.disabled) {

            // Does the selector visible?

            if (this.state.showselector && event) {
                event.preventDefault();
                event.stopPropagation();
            } else {

                // Showing the selector

                this.clearError();
                window.document.body.addEventListener('click', this._hideSelector);
                this.setState({ showselector: true });
            }
        }
    }

    /**
     * Hides the selector.
     * 
     * @param   {Object}    event       event object
     */

    _hideSelector = (event) => {

        // Does the selector visible?

        if (this.state.showselector) {

            // Selector closer

            let closeSelector = () => {
                window.document.body.removeEventListener('click', this._hideSelector);
                this.setState({ showselector: false });
                let comboValue;

                if (this.state.selecteditem) {
                    comboValue = this.props.nameattr ? this.state.selecteditem[this.props.nameattr] : this.state.selecteditem;
                } else {
                    comboValue = '';
                }

                if (this.props.lookup) {
                    this.input.value = comboValue;
                } else {
                    this.input.innerHTML = comboValue;
                }
            };

            if (event) {

                // Getting the clicked node

                let node = event.target;

                while (node && this.selector !== node && this.selectorbutton !== node) {
                    node = node.parentNode;
                }

                // Closing the selector

                if (!node) {
                    event.preventDefault();
                    event.stopPropagation();
                    closeSelector();
                }
            } else {

                // Closing the selector

                closeSelector();
            }
        }
    }

    _selectItem = (event) => {
        let index = event.target.getAttribute('data-index');
        this.setState({selecteditem: this.props.source[index]}, () => {
            let comboValue = this.props.idattr ? this.props.source[index][this.props.nameattr] : this.props.source[index];
            
            if (this.props.lookup) {
                this.input.value = comboValue;
            } else {
                this.input.innerHTML = comboValue;
            }

            this._hideSelector();

            if (this.props.onSelect) {
                this.props.onSelect(this.props.source[index]);
            }
        });
    }

    _startTyping = (event) => {
        if (!this._isEnter(event)) {

            // Getting the code of the pressed key
            
            let keycode = event.keyCode ? event.keyCode : event.which;

            // Allow BACKSPACE, DELETE, TAB, ESCAPE, ENTER

            /*if ([46, 8, 9, 27, 13, 110].indexOf(keycode) >= 0) {
                return;
            }*/

            // Allow: Ctrl/cmd+A

            if ((keycode === 65 && (event.ctrlKey === true || event.metaKey === true)) ||
              // Allow: Ctrl/cmd+C
              (keycode === 67 && (event.ctrlKey === true || event.metaKey === true)) ||
              // Allow: Ctrl/cmd+X
              (keycode === 88 && (event.ctrlKey === true || event.metaKey === true)) ||
              // Allow: Ctrl/cmd+V
              (keycode === 86 && (event.ctrlKey === true || event.metaKey === true))) {
              return;
            }

            // Allow: HOME, END, LEFT, RIGHT

            if (keycode >= 35 && keycode <= 39) {
                return;
            }
            
            this._showSelector();
        }
    }

    /**
     * Combobox renderer.
     */

    render() {

        // Setting default label position

        let labelposition = this.props.labelposition || Combobox.LABELPOSITION.LEFT;
        let width = (this.props.width || 200) - 28;

        // Rendering

        return (

            /* Combobox container */

            <div className="combobox" style={this.getStyle()} title={this.props.tooltip}>
                <div className={`${this.props.label && labelposition === Combobox.LABELPOSITION.LEFT ? 'horizontal' : 'vertical'}`}>

                    {/* Label */}
                
                    { this.props.label && <div><Label text={this.props.label} topgap={labelposition === Combobox.LABELPOSITION.TOP ? 0 : 6} rightgap={this.props.labelwidth || labelposition === Combobox.LABELPOSITION.TOP ? 0 : 8} width={this.props.labelwidth} isrequired={this.props.isrequired} requiredmessage={this.props.requiredmessage} /></div>}
                    
                    <div className="combobox-container">

                        {/* Inputbox or selector */}

                        { this.props.lookup ?
                            <div className="horizontal">
                                <input 
                                    ref={c => {this.input = c;}} 
                                    type="text"
                                    disabled={this.props.disabled}
                                    placeholder={this.props.placeholder}
                                    className={`combobox-input ${this.state.error ? 'combobox-input-invalid' : ''} combobox-input-${this.props.disabled ? 'disabled' : 'enabled'}`}
                                    maxLength={this.props.maxlength || 1000}
                                    style={{ width: width + 'px', textTransform: this.props.touppercase ? 'uppercase' : this.props.tocapitalize ? 'capitalize' : 'none' }}
                                    onFocus={(event) => {event.target.select();}}
                                    onKeyDown={this._startTyping}
                                    onKeyUp={this._validateOnTyping}
                                    onBlur={this._validateOnBlur}
                                />
                                <div className={`combobox-opener ${this.props.disabled ? 'combobox-opener-disabled' : ''}`} ref={c => {this.selectorbutton = c;}} onClick={this._showSelector}>
                                    <img alt="Open" src={OpenIcon} className={`combobox-opener-icon ${this.props.disabled ? 'combobox-opener-icon-disabled' : 'combobox-opener-icon-enabled'}`} />
                                </div>
                            </div>
                            :
                            <div className={`${this.props.disabled ? '' : 'combobox-dropdown-container'} horizontal`} ref={c => {this.selectorbutton = c;}} onClick={this._showSelector}>
                                <div
                                    ref={c => {this.input = c;}}
                                    className={`combobox-dropdown ${this.state.error ? 'combobox-dropdown-invalid' : ''} combobox-dropdown-${this.props.disabled ? 'disabled' : 'enabled'}`}
                                    style={{ width: width + 'px' }}
                                />
                                <div className={`combobox-opener ${this.props.disabled ? 'combobox-opener-disabled' : ''}`}>
                                    <img alt="Open" src={OpenIcon} className={`combobox-opener-icon ${this.props.disabled ? 'combobox-opener-icon-disabled' : 'combobox-opener-icon-enabled'}`} />
                                </div>
                            </div>
                        }

                        {/* Selector */}

                        { this.state.showselector && <div ref={c => {this.selector = c;}} className="combobox-selector" style={{ width: (width + 28 + 28) + 'px' }}>
                            { this.props.source.map((item, index) =>
                                ((this.props.lookup && (this.input.value === '' || (this.props.nameattr ? item[this.props.nameattr] : item).indexOf(this.input.value) >= 0)) || !this.props.lookup) &&
                                    <div key={ this.props.idattr ? item[this.props.idattr] : index } data-index={index} className="combobox-item" style={{ width: (width + 28) + 'px' }} onClick={this._selectItem}>{ this.props.nameattr ? item[this.props.nameattr] : item }</div>
                            )}
                        </div> }

                        {/* Comment */}

                        { this.props.comment && <div className="combobox-comment">{this.props.comment}</div> }

                        {/* Error message */}

                        {this.state.error && (<div className={`combobox-error ${this.state.showerror ? 'combobox-error-show' : ''}`} style={{ top: (this.props.comment ? -4 : 8) + 'px'}}>
                            <div className="combobox-error-container">
                                <div className="combobox-error-content">{this.state.error}</div>
                            </div>
                        </div>)}
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Combobox/Combobox.js